import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  ScrollView,
  TouchableOpacity,
  Image,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import axios from "axios";
import { FASTAPI_BASE_URL } from "../config";
import MarkdownRenderer from "./MarkdownRenderer";
import ChatPanel from "./ChatPanel";
import { shouldSwitchToActive } from "../utils/railPanelMode";
import { MaterialCommunityIcons } from "@expo/vector-icons";


// BDD 12/2/2025 - For logging users clicking on suggestionrail questions
// Responsive width based on viewport - adapts to browser zoom
export const SUGGESTION_RAIL_WIDTH = 320; // Base width for calculations (legacy, actual width is now 40% of viewport)

// Threshold for compact mode (below this width, use compact styles)
const COMPACT_THRESHOLD = 1536;

// Helper to get responsive rail width based on viewport (redesign: 40% target)
export const getResponsiveRailWidth = (viewportWidth) => {
  if (viewportWidth < 1100) return 0;                          // Hide completely at narrow viewports
  if (viewportWidth < 1280) return Math.round(viewportWidth * 0.30); // Medium screens - 30%
  if (viewportWidth < 1536) return Math.round(viewportWidth * 0.35); // Large screens - 35%
  return Math.round(viewportWidth * 0.40);                     // Desktop baseline - 40% (redesign: 20/40/40 split)
};

// Helper to check if we're in compact mode
export const isCompactMode = (viewportWidth) => viewportWidth < COMPACT_THRESHOLD;

function SuggestionRail({
  t,
  isDarkMode,
  sparksMapped,
  suggestedQuestions,
  suggestedQuestionsHeader,
  idleSubtitle = null,
  onAsk,
  viewMode = "collections", // 👈 collections vs curriculum
  userId,                   // 👈 NEW: for logging
  activeCollectionId,       // 👈 NEW: for logging
    // ✅ NEW
  suggestedQuestionsMeta, // BDD 01/15/2026 - Summarize this topic AI ask
  customWidth = null, // Optional: user-controlled width from drag resize
  // Right panel chat input + toggles
  answerStyle,
  setAnswerStyle,
  inputValue,
  setInputValue,
  onInputSubmit,
  placeholder,
  loading, // AI is generating a response
  // Chat history
  chatSessions,
  sessionsLoading,
  selectedConversationId,
  openChatSession,
  closeChatSession,
  sessionTurnsLoading,
  formatLocalDate,
  history,
  renderHistorySection,
  authToken,
  handleVideoClick,
  handlePWOClick,
  handleChapterClick,
  handleCollectionClick,
  onVoiceInput,
  isListening,
  styles: appStyles,
  // Audio playback props (passed through to ChatPanel)
  isMuted,
  isPlaying,
  audioUrl,
  onToggleMute,
  onPlayAnswer,
  onStopAudio,
}) {
  // Panel mode: "idle" (suggestions), "active" (chatting), "history" (past sessions), "browse" (collection folder nav)
  const [panelMode, setPanelMode] = useState("idle");
  // Get responsive width based on viewport (for browser zoom support)
  const { width: viewportWidth } = useWindowDimensions();
  const responsiveWidth = getResponsiveRailWidth(viewportWidth);
  // Use customWidth if provided (from drag resize), otherwise use responsive width
  const railWidth = customWidth !== null ? customWidth : responsiveWidth;
  const showRail = responsiveWidth > 0; // Hide rail at narrow viewports (< 960px)
  const isCompact = isCompactMode(viewportWidth);

  // Dynamic styles based on zoom level
  const dynamicStyles = {
    paddingTop: isCompact ? 6 : 10,
    paddingLeft: isCompact ? 6 : 8,
    paddingRight: isCompact ? 6 : 8,
    paddingBottom: isCompact ? 12 : 20,
    headerMarginBottom: isCompact ? 8 : 12,
    headerPaddingBottom: isCompact ? 6 : 8,
    headerFontSize: isCompact ? 13 : 15,
    headerLetterSpacing: isCompact ? 0.4 : 0.5,
    cardGap: isCompact ? 6 : 10,
    cardPaddingVertical: isCompact ? 10 : 14,
    cardPaddingHorizontal: isCompact ? 12 : 16,
    questionFontSize: isCompact ? 14 : 15.5,
    questionLineHeight: isCompact ? 18 : 21,
    sectionMarginBottom: isCompact ? 16 : 24,
  };

  // Auto-switch to active mode when history has entries
  const hasActiveConversation = Array.isArray(history) && history.length > 0;
  useEffect(() => {
    if (
      shouldSwitchToActive({
        panelMode,
        hasActiveConversation,
        selectedConversationId,
      })
    ) {
      setPanelMode("active");
    }
    // Allow Chat tab to stay active with empty state (centered suggestions)
  }, [hasActiveConversation, selectedConversationId]);

  // BDD 02/11/2026 - Chapter level Curiosity Sparks
  const hasSparks = Array.isArray(sparksMapped) && sparksMapped.length > 0;
  const hasSuggestions = Array.isArray(suggestedQuestions) && suggestedQuestions.length > 0;
  const hasChatHistory = Array.isArray(chatSessions) && chatSessions.length > 0;

  // All suggestion/spark items combined for pill display
  const allSuggestionItems = [
    ...(suggestedQuestions || []).map((q) => ({ type: "suggestion", text: q })),
    ...(sparksMapped || []).map((s) => ({
      type: "spark",
      text: typeof s === "string" ? s : s?.label || s?.text || "",
      raw: s,
    })),
  ].filter((item) => item.text);

  // Don't render if no content or if viewport is too narrow
  // if (!hasSparks && !hasSuggestions) return null; // BDD 04/14/2026 - do not do this
  if (!showRail) return null;

  // --- helper: log collection prompt engagement (non-blocking) ---
  const logCollectionPromptEngagement = (spark, label) => {
    if (!userId || typeof FASTAPI_BASE_URL === "undefined" || !FASTAPI_BASE_URL) return;
    if (typeof axios === "undefined" || !axios) return;
    try {
      const collection_prompt_id = spark?.id ?? spark?.collection_prompt_id ?? spark?.prompt_id ?? null;
      const context_kind = spark?.context_kind ?? spark?.level ?? "collection";
      const body = {
        collection_id: spark?.collection_id ?? activeCollectionId ?? null,
        collection_prompt_id,
        context_kind,
        prompt_text: label,
      };
      axios
        .post(`${FASTAPI_BASE_URL}/student/${encodeURIComponent(userId)}/collection-prompt-engagement`, body)
        .catch((err) => console.error("[SuggestionRail] Failed to log collection prompt engagement", err));
    } catch (err) {
      console.error("[SuggestionRail] Unexpected error building prompt engagement payload", err);
    }
  };

  const handleSparkPress = (spark) => {
    const label = typeof spark === "string" ? spark : spark?.label || spark?.text || "";
    if (!label) return;
    setPanelMode("active");
    let customInstructions;
    if (viewMode === "collections") {
      customInstructions = {
        mode: "collection_curiosity",
        collection_id: spark?.collection_id ?? activeCollectionId ?? null,
        context_kind: spark?.context_kind ?? spark?.level ?? "collection",
      };
    }
    if (customInstructions) {
      onAsk(label, { customInstructions });
    } else {
      onAsk(label);
    }
    if (viewMode === "collections") logCollectionPromptEngagement(spark, label);
  };

  const handlePillPress = (item) => {
    if (item.type === "spark") {
      // handleSparkPress sets panelMode internally
      handleSparkPress(item.raw || item.text);
    } else {
      setPanelMode("active");
      const isTopicSuggestion = suggestedQuestionsMeta?.kind === "topic" && !!suggestedQuestionsMeta?.topicScope;
      if (isTopicSuggestion) {
        onAsk(item.text, { topicScope: suggestedQuestionsMeta.topicScope, forceNewDialog: true });
      } else {
        onAsk(item.text);
      }
    }
  };

  return (
    <View
      style={{
        flex: 1,
        width: railWidth,
        borderLeftWidth: 1,
        borderLeftColor: isDarkMode ? "#374151" : "#e2e8f0",
        backgroundColor: isDarkMode ? "#020617" : "#fff",
        position: "relative",
      }}
    >
      {/* ====== PANEL HEADER ====== */}
      <View
        style={{
          paddingVertical: isCompact ? 10 : 14,
          paddingHorizontal: isCompact ? 10 : 14,
          borderBottomWidth: 1,
          borderBottomColor: isDarkMode ? "rgba(148,163,184,0.1)" : "#f1f5f9",
          background: isDarkMode
            ? "linear-gradient(135deg, rgba(15,23,42,0.8) 0%, rgba(2,6,23,1) 60%)"
            : "linear-gradient(135deg, #eff6ff 0%, #fff 60%)",
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flex: 1 }}>
            <Image
              source={require("../../assets/Saras-3D_logo_bird.png")}
              style={{
                width: isCompact ? 28 : 34,
                height: isCompact ? 28 : 34,
                borderRadius: 10,
              }}
              resizeMode="contain"
            />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: isCompact ? 14 : 15, fontWeight: "700", color: isDarkMode ? "#f1f5f9" : "#0f172a" }}>
                Saras AI
              </Text>
              <Text style={{ fontSize: isCompact ? 11 : 13, color: isDarkMode ? "#64748b" : "#94a3b8" }}>
                {panelMode === "active"
                  ? (loading ? t("Thinking…") : t("Chat active"))
                  : panelMode === "history"
                    ? t("Past conversations")
                    : idleSubtitle
                      ? t(idleSubtitle)
                      : viewMode === "curriculum"
                        ? t("Curiosity Sparks · Chapter")
                        : t("Curiosity Sparks")}
              </Text>
            </View>
          </View>

          {/* Header action buttons */}
          <View style={{ flexDirection: "row", gap: 6 }}>
            {/* Short/Detailed compact toggles in header (active mode) */}
            {panelMode === "active" && answerStyle && setAnswerStyle && (
              <View style={{ flexDirection: "row", borderRadius: 6, overflow: "hidden", borderWidth: 1, borderColor: isDarkMode ? "rgba(148,163,184,0.15)" : "#e2e8f0" }}>
                {[
                  { key: "concise", label: t("Short") },
                  { key: "detailed", label: t("Detailed") },
                ].map((m) => (
                  <TouchableOpacity
                    key={m.key}
                    onPress={() => setAnswerStyle(m.key)}
                    style={{
                      paddingVertical: 4,
                      paddingHorizontal: isCompact ? 8 : 10,
                      backgroundColor: answerStyle === m.key
                        ? (isDarkMode ? "rgba(37,99,235,0.2)" : "#eff6ff")
                        : "transparent",
                    }}
                  >
                    <Text style={{
                      fontSize: isCompact ? 12 : 13,
                      fontWeight: "700",
                      color: answerStyle === m.key
                        ? (isDarkMode ? "#60a5fa" : "#2563eb")
                        : (isDarkMode ? "#475569" : "#94a3b8"),
                    }}>
                      {m.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        </View>
      </View>

      {/* ====== TAB BAR ====== */}
      <View
        style={{
          flexDirection: "row",
          gap: 6,
          paddingHorizontal: isCompact ? 8 : 12,
          paddingVertical: isCompact ? 6 : 8,
          borderBottomWidth: 1,
          borderBottomColor: isDarkMode ? "rgba(148,163,184,0.1)" : "#f1f5f9",
        }}
      >
        {[
          { key: "idle", label: t("Suggested Questions"), icon: "\u2728" },
          { key: "active", label: t("Chat"), icon: "\uD83D\uDCAC", dot: hasActiveConversation },
          { key: "history", label: t("History"), icon: "\uD83D\uDCCB" },
        ].map((tab) => {
          const isActiveTab = panelMode === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              onPress={() => setPanelMode(tab.key)}
              activeOpacity={0.7}
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                paddingVertical: isCompact ? 6 : 8,
                borderRadius: 8,
                backgroundColor: isActiveTab
                  ? (isDarkMode ? "rgba(37,99,235,0.15)" : "#eff6ff")
                  : "transparent",
                borderWidth: 1,
                borderColor: isActiveTab
                  ? (isDarkMode ? "rgba(96,165,250,0.3)" : "#bfdbfe")
                  : (isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.15)"),
                borderBottomWidth: isActiveTab ? 2 : 1,
                borderBottomColor: isActiveTab
                  ? (isDarkMode ? "#60a5fa" : "#2563eb")
                  : (isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.15)"),
              }}
            >
              <Text style={{ fontSize: isCompact ? 13 : 15 }}>{tab.icon}</Text>
              <Text style={{
                fontSize: isCompact ? 12 : 13,
                fontWeight: "600",
                color: isActiveTab
                  ? (isDarkMode ? "#60a5fa" : "#2563eb")
                  : (isDarkMode ? "#64748b" : "#94a3b8"),
              }}>
                {tab.label}
              </Text>
              {tab.dot && (
                <View style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#22c55e",
                  marginLeft: 2,
                }} />
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* ====== MAIN CONTENT AREA ====== */}
      <View style={{ flex: 1, paddingTop: dynamicStyles.paddingTop, paddingLeft: dynamicStyles.paddingLeft }}>

        {/* ===== IDLE STATE: Full suggestion cards ===== */}
        {panelMode === "idle" && (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingRight: dynamicStyles.paddingRight,
              paddingBottom: dynamicStyles.paddingBottom,
            }}
            showsVerticalScrollIndicator={true}
          >
            {/* 🤖 Follow-up suggestions (Deepen your understanding) */}
            {hasSuggestions && (
              <View style={{ marginBottom: hasSparks ? dynamicStyles.sectionMarginBottom : 0 }}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: dynamicStyles.headerMarginBottom,
                    paddingBottom: dynamicStyles.headerPaddingBottom,
                    borderBottomWidth: 1,
                    borderBottomColor: isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.15)",
                  }}
                >
                  <Text style={{
                    fontSize: dynamicStyles.headerFontSize,
                    fontWeight: "600",
                    letterSpacing: dynamicStyles.headerLetterSpacing,
                    textTransform: "uppercase",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                  }}>
                    {`🤖 ${Array.isArray(suggestedQuestionsHeader) && suggestedQuestionsHeader.length > 0
                      ? suggestedQuestionsHeader[0]
                      : t("Deepen your understanding:")}`}
                  </Text>
                </View>
                <Text style={{
                  marginTop: 6,
                  marginBottom: 10,
                  fontSize: 14,
                  color: isDarkMode ? "rgba(148,163,184,0.85)" : "rgba(100,116,139,0.95)",
                }}>
                  {t("Tap a question to ask the AI")}
                </Text>

                <View style={{ flexDirection: "column", gap: dynamicStyles.cardGap }}>
                  {suggestedQuestions.map((question, index) => (
                    <TouchableOpacity
                      key={index}
                      onPress={() => {
                        setPanelMode("active");
                        const isTopicSuggestion = suggestedQuestionsMeta?.kind === "topic" && !!suggestedQuestionsMeta?.topicScope;
                        if (isTopicSuggestion) {
                          onAsk(question, { topicScope: suggestedQuestionsMeta.topicScope, forceNewDialog: true });
                        } else {
                          onAsk(question);
                        }
                      }}
                      activeOpacity={0.7}
                      style={{
                        backgroundColor: isDarkMode ? "#0f172a" : "#ffffff",
                        borderRadius: 12,
                        paddingVertical: dynamicStyles.cardPaddingVertical,
                        paddingHorizontal: dynamicStyles.cardPaddingHorizontal,
                        borderWidth: 1,
                        borderColor: isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.2)",
                        shadowColor: isDarkMode ? "#000" : "#64748b",
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: isDarkMode ? 0.3 : 0.08,
                        shadowRadius: 8,
                        elevation: 2,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <View style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, backgroundColor: isDarkMode ? "#8b5cf6" : "#a78bfa", opacity: 0.6 }} />
                      <View style={{ paddingLeft: 8 }}>
                        <div className="suggestion-question-markdown" style={{
                          fontSize: dynamicStyles.questionFontSize,
                          lineHeight: `${dynamicStyles.questionLineHeight}px`,
                          color: isDarkMode ? "#e2e8f0" : "#1e293b",
                        }}>
                          <MarkdownRenderer content={question} />
                        </div>
                        <View style={{ flexDirection: "row", justifyContent: "flex-end", marginTop: 6 }}>
                          <View style={{
                            paddingVertical: 3,
                            paddingHorizontal: 10,
                            borderRadius: 999,
                            backgroundColor: isDarkMode ? "rgba(167,139,250,0.12)" : "rgba(124,58,237,0.10)",
                            borderWidth: 1,
                            borderColor: isDarkMode ? "rgba(167,139,250,0.25)" : "rgba(124,58,237,0.18)",
                          }}>
                            <Text style={{ fontSize: 13, fontWeight: "700", color: isDarkMode ? "#a78bfa" : "#7c3aed", opacity: 0.95 }}>
                              {t("Ask AI")} →
                            </Text>
                          </View>
                        </View>
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}

            {/* ✨ Curiosity Sparks */}
            {hasSparks && (
              <View>
                <View style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: dynamicStyles.headerMarginBottom,
                  paddingBottom: dynamicStyles.headerPaddingBottom,
                  borderBottomWidth: 1,
                  borderBottomColor: isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.15)",
                }}>
                  <Text style={{
                    fontSize: dynamicStyles.headerFontSize,
                    fontWeight: "600",
                    letterSpacing: dynamicStyles.headerLetterSpacing,
                    textTransform: "uppercase",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                  }}>
                    ✨ {viewMode === "curriculum" ? t("Curiosity Sparks (Chapter)") : t("Curiosity Sparks")}
                  </Text>
                </View>
                <Text style={{
                  marginTop: 6, marginBottom: 10, fontSize: 14,
                  color: isDarkMode ? "rgba(148,163,184,0.85)" : "rgba(100,116,139,0.95)",
                }}>
                  {t("Tap a question to ask the AI")}
                </Text>
                <View style={{ flexDirection: "column", gap: dynamicStyles.cardGap }}>
                  {sparksMapped.map((item, idx) => {
                    const label = typeof item === "string" ? item : item.label || item.text || "";
                    if (!label) return null;
                    return (
                      <TouchableOpacity
                        key={idx}
                        onPress={() => handleSparkPress(item)}
                        activeOpacity={0.7}
                        style={{
                          backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc",
                          borderRadius: 10,
                          paddingVertical: dynamicStyles.cardPaddingVertical,
                          paddingHorizontal: dynamicStyles.cardPaddingHorizontal,
                          borderWidth: 1,
                          borderColor: isDarkMode ? "rgba(148,163,184,0.1)" : "#e2e8f0",
                          ...(Platform.OS === "web" ? { transition: "background 0.15s, border-color 0.15s" } : {}),
                        }}
                      >
                        <div className="suggestion-question-markdown" style={{
                          fontSize: dynamicStyles.questionFontSize,
                          lineHeight: `${dynamicStyles.questionLineHeight}px`,
                          color: isDarkMode ? "#e2e8f0" : "#334155",
                          marginBottom: 8,
                        }}>
                          <MarkdownRenderer content={label} />
                        </div>
                        <Text style={{ fontSize: 13, fontWeight: "600", color: isDarkMode ? "#60a5fa" : "#2563eb" }}>
                          {t("Ask Saras AI")} →
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            )}

            {/* Response Type Toggles (idle) */}
            {answerStyle && setAnswerStyle && (
              <View style={{ marginTop: 18, paddingRight: dynamicStyles.paddingRight }}>
                <Text style={{
                  fontSize: isCompact ? 12 : 13, fontWeight: "600", letterSpacing: 0.8,
                  textTransform: "uppercase", color: isDarkMode ? "#64748b" : "#94a3b8", marginBottom: 8,
                }}>
                  {t("Response Type")}
                </Text>
                <View style={{ flexDirection: "row", gap: 6, marginBottom: 8 }}>
                  {[
                    { key: "concise", label: t("Short") },
                    { key: "detailed", label: t("Detailed") },
                  ].map((m) => (
                    <TouchableOpacity
                      key={m.key}
                      onPress={() => setAnswerStyle(m.key)}
                      style={{
                        flex: 1, paddingVertical: 6, borderRadius: 8, borderWidth: 1,
                        borderColor: answerStyle === m.key ? (isDarkMode ? "#60a5fa" : "#2563eb") : (isDarkMode ? "rgba(148,163,184,0.15)" : "#e2e8f0"),
                        backgroundColor: answerStyle === m.key ? (isDarkMode ? "rgba(37,99,235,0.15)" : "#eff6ff") : "transparent",
                        alignItems: "center",
                      }}
                    >
                      <Text style={{
                        fontSize: 14, fontWeight: "600",
                        color: answerStyle === m.key ? (isDarkMode ? "#60a5fa" : "#2563eb") : (isDarkMode ? "#64748b" : "#94a3b8"),
                      }}>
                        {m.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </ScrollView>
        )}

        {/* ===== ACTIVE STATE: Chat panel (extracted component) ===== */}
        {panelMode === "active" && (
          <ChatPanel
            t={t}
            isDarkMode={isDarkMode}
            isCompact={isCompact}
            dynamicStyles={dynamicStyles}
            history={history}
            loading={loading}
            allSuggestionItems={allSuggestionItems}
            handlePillPress={handlePillPress}
            authToken={authToken}
            handleVideoClick={handleVideoClick}
            handlePWOClick={handlePWOClick}
            handleChapterClick={handleChapterClick}
            handleCollectionClick={handleCollectionClick}
            isMuted={isMuted}
            isPlaying={isPlaying}
            audioUrl={audioUrl}
            onToggleMute={onToggleMute}
            onPlayAnswer={onPlayAnswer}
            onStopAudio={onStopAudio}
          />
        )}

        {/* ===== HISTORY STATE: Past sessions ===== */}
        {panelMode === "history" && (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingRight: dynamicStyles.paddingRight,
              paddingBottom: 20,
            }}
            showsVerticalScrollIndicator={true}
          >
            {selectedConversationId == null ? (
              <>
                {sessionsLoading ? (
                  <ActivityIndicator style={{ marginTop: 20 }} />
                ) : !hasChatHistory ? (
                  <Text style={{
                    fontSize: 14, color: isDarkMode ? "#64748b" : "#94a3b8",
                    textAlign: "center", marginTop: 20, paddingHorizontal: 12,
                  }}>
                    {t("No AI chats yet. Ask a question to get started.")}
                  </Text>
                ) : (
                  (chatSessions || []).map((s, sessionIdx) => {
                    // First session is the most recent — if there's an active conversation, clicking it should go to active chat mode
                    const isCurrentSession = sessionIdx === 0 && hasActiveConversation;
                    return (
                    <TouchableOpacity
                      key={s.conversation_id}
                      onPress={() => {
                        if (isCurrentSession) {
                          // Switch to active chat (live conversation with suggestion pills)
                          setPanelMode("active");
                        } else {
                          openChatSession(s.conversation_id);
                        }
                      }}
                      style={{
                        padding: isCompact ? 8 : 10,
                        marginBottom: 8,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isCurrentSession
                          ? (isDarkMode ? "rgba(96,165,250,0.35)" : "#bfdbfe")
                          : (isDarkMode ? "rgba(148,163,184,0.15)" : "#e2e8f0"),
                        backgroundColor: isCurrentSession
                          ? (isDarkMode ? "rgba(37,99,235,0.1)" : "#eff6ff")
                          : (isDarkMode ? "rgba(15,23,42,0.45)" : "#f8fafc"),
                      }}
                    >
                      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                        <Text style={{ fontSize: isCompact ? 13 : 14, fontWeight: "700", color: isDarkMode ? "#e2e8f0" : "#0f172a", flex: 1 }}>
                          {t("Chat")} • {formatLocalDate ? formatLocalDate(s.last_activity_at || s.created_at || new Date()) : ""}
                        </Text>
                        {isCurrentSession && (
                          <View style={{
                            paddingVertical: 2, paddingHorizontal: 8, borderRadius: 10,
                            backgroundColor: isDarkMode ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)",
                            borderWidth: 1,
                            borderColor: isDarkMode ? "rgba(34,197,94,0.3)" : "rgba(34,197,94,0.25)",
                          }}>
                            <Text style={{ fontSize: 11, fontWeight: "700", color: isDarkMode ? "#4ade80" : "#16a34a" }}>
                              {t("Active")}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={{ marginTop: 4, fontSize: isCompact ? 12 : 13, color: isDarkMode ? "#94a3b8" : "#475569" }}>
                        {Math.floor((s.turn_count ?? 0) / 2)} {Math.floor((s.turn_count ?? 0) / 2) === 1 ? t("question") : t("questions")} • {(s.snippet || "").trim()}
                      </Text>
                      {s.content_db ? (
                        <Text style={{ marginTop: 4, fontSize: 13, opacity: 0.75, color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                          {t("Board:")} {String(s.content_db).toUpperCase()}
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                  );
                  })
                )}
              </>
            ) : (
              <>
                <TouchableOpacity onPress={closeChatSession} style={{ marginBottom: 10 }}>
                  <Text style={{ color: isDarkMode ? "#60a5fa" : "#2563eb", fontWeight: "700", fontSize: 14 }}>
                    ← {t("Back to sessions")}
                  </Text>
                </TouchableOpacity>
                {sessionTurnsLoading ? (
                  <ActivityIndicator />
                ) : (history || []).map((entry, index) => (
                  <View key={index} style={{ marginBottom: 14 }}>
                    {/* User question bubble — right-aligned, blue */}
                    {entry?.question ? (
                      <View style={{ alignItems: "flex-end", marginBottom: 8 }}>
                        <View style={{
                          maxWidth: "85%",
                          backgroundColor: "#2563eb",
                          borderRadius: 12,
                          borderBottomRightRadius: 2,
                          paddingVertical: 10,
                          paddingHorizontal: 14,
                        }}>
                          <div className="rail-bubble-user-markdown"><MarkdownRenderer content={entry.question} /></div>
                        </View>
                      </View>
                    ) : null}

                    {/* AI response bubble — left-aligned, gray */}
                    {entry?.answer ? (
                      <View style={{ alignItems: "flex-start" }}>
                        <Text style={{ fontSize: 12, color: isDarkMode ? "#64748b" : "#94a3b8", marginBottom: 4, marginLeft: 4 }}>
                          Saras AI
                        </Text>
                        <View style={{
                          maxWidth: "92%",
                          backgroundColor: isDarkMode ? "#1e293b" : "#f8fafc",
                          borderRadius: 12,
                          borderBottomLeftRadius: 2,
                          paddingVertical: 10,
                          paddingHorizontal: 14,
                          borderWidth: 1,
                          borderColor: isDarkMode ? "rgba(148,163,184,0.15)" : "#e2e8f0",
                        }}>
                          <div className="rail-bubble-markdown"><MarkdownRenderer content={entry.answer} /></div>

                          {/* Guide Me actions */}
                          {Array.isArray(entry?.guideMeActions) && entry.guideMeActions.length > 0 && (
                            <View style={{ marginTop: 10 }}>
                              <Text style={{ fontSize: 14, fontWeight: "800", color: isDarkMode ? "#cbd5e1" : "#334155" }}>
                                {"🧭"} Next actions
                              </Text>
                              <View style={{ marginTop: 6, gap: 6 }}>
                                {entry.guideMeActions.map((a, i) => {
                                  const kind = String(a?.kind || "").toLowerCase();
                                  const label = a?.name || (kind === "video" ? "Video" : "PWO");
                                  const topic = a?.topic_name || null;
                                  return (
                                    <TouchableOpacity
                                      key={`gm-${i}`}
                                      onPress={() => {
                                        if (kind === "video") handleVideoClick?.(a?.video_id ?? a?.id, a?.tag_id ?? null, authToken, null, a?.source_topic_id ?? null, false);
                                        else if (kind === "pwo") handlePWOClick?.(a?.pwo_id ?? a?.id, a?.scene_key || a?.scene_name || "mainmenu", authToken, null, null, a?.source_topic_id ?? null, false);
                                      }}
                                      style={{
                                        paddingVertical: 8, paddingHorizontal: 10, borderRadius: 10,
                                        borderWidth: 1, borderColor: isDarkMode ? "rgba(148,163,184,0.18)" : "rgba(148,163,184,0.28)",
                                        backgroundColor: isDarkMode ? "rgba(15,23,42,0.55)" : "rgba(255,255,255,0.85)",
                                      }}
                                    >
                                      <Text style={{ fontSize: 14, fontWeight: "700", color: isDarkMode ? "#e2e8f0" : "#0f172a" }}>
                                        {kind === "video" ? "🎥 " : "🎮 "}{label}
                                      </Text>
                                      {topic && <Text style={{ fontSize: 13, color: isDarkMode ? "#94a3b8" : "#475569", marginTop: 2 }}>Topic: {topic}</Text>}
                                    </TouchableOpacity>
                                  );
                                })}
                              </View>
                            </View>
                          )}

                          {/* Video references */}
                          {Array.isArray(entry?.videos) && entry.videos.length > 0 && (
                            <View style={{ marginTop: 10 }}>
                              <Text style={{ fontSize: 13, fontWeight: "700", color: isDarkMode ? "#94a3b8" : "#64748b", marginBottom: 4 }}>
                                🎬 {t("Video References:")}
                              </Text>
                              {entry.videos.map((video, i) => (
                                <TouchableOpacity key={i} onPress={() => handleVideoClick?.(video?.videoID, video?.tagID, authToken)}>
                                  <Text style={{ fontSize: 14, color: isDarkMode ? "#60a5fa" : "#2563eb", marginBottom: 2 }}>
                                    🎞 {video?.name ? `${video.name} - ${video.tag_name || ""}` : (video?.tag_name || "Video")}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          )}

                          {/* PWO / 3D Simulation references */}
                          {Array.isArray(entry?.pwos) && entry.pwos.length > 0 && (
                            <View style={{ marginTop: 10 }}>
                              <Text style={{ fontSize: 13, fontWeight: "700", color: isDarkMode ? "#94a3b8" : "#64748b", marginBottom: 4 }}>
                                🎮 {t("3D Simulations:")}
                              </Text>
                              {entry.pwos.map((pwo, i) => (
                                <TouchableOpacity key={i} onPress={() => handlePWOClick?.(pwo?.pwoID, pwo?.pwo_scene_name || "mainmenu", authToken)}>
                                  <Text style={{ fontSize: 14, color: isDarkMode ? "#60a5fa" : "#2563eb", marginBottom: 2 }}>
                                    🎮 {pwo?.pwo_name || "3D Simulation"} - {pwo?.pwo_scene_label || pwo?.pwo_scene_name || "Scene"}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          )}

                          {/* Chapter references */}
                          {Array.isArray(entry?.chapters) && entry.chapters.length > 0 && (
                            <View style={{ marginTop: 10 }}>
                              <Text style={{ fontSize: 13, fontWeight: "700", color: isDarkMode ? "#94a3b8" : "#64748b", marginBottom: 4 }}>
                                📚 {t("Chapters:")}
                              </Text>
                              {entry.chapters.map((ch, i) => (
                                <TouchableOpacity key={i} onPress={() => handleChapterClick?.(ch)}>
                                  <Text style={{ fontSize: 14, color: isDarkMode ? "#60a5fa" : "#2563eb", marginBottom: 2 }}>
                                    📖 {ch?.chapter_name || ch?.name || `Chapter ${ch?.chapterId || ch?.id}`}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          )}

                          {/* Collection references */}
                          {Array.isArray(entry?.collections) && entry.collections.length > 0 && (() => {
                            const scoped = entry.collections.find((c) => c && c.scoped);
                            const others = entry.collections.filter((c) => c && !c.scoped);
                            return (
                              <View style={{ marginTop: 10 }}>
                                {scoped && (
                                  <View style={{ marginBottom: others.length ? 6 : 0 }}>
                                    <Text style={{ fontSize: 13, fontWeight: "700", color: isDarkMode ? "#94a3b8" : "#64748b", marginBottom: 4 }}>
                                      📌 {t("Current module:")}
                                    </Text>
                                    <TouchableOpacity onPress={() => handleCollectionClick?.(scoped)}>
                                      <Text style={{ fontSize: 14, color: isDarkMode ? "#60a5fa" : "#2563eb" }}>
                                        🧩 {scoped?.display_name || scoped?.name_localized || scoped?.localized_name || scoped?.name || `Collection ${scoped?.collection_id || scoped?.id}`}                                      </Text>
                                    </TouchableOpacity>
                                  </View>
                                )}
                                {others.length > 0 && (
                                  <View>
                                    <Text style={{ fontSize: 13, fontWeight: "700", color: isDarkMode ? "#94a3b8" : "#64748b", marginBottom: 4 }}>
                                      🗂️ {t("Collections:")}
                                    </Text>
                                    {others.map((c, i) => (
                                      <TouchableOpacity key={`${c?.collection_id || c?.id || i}`} onPress={() => handleCollectionClick?.(c)}>
                                        <Text style={{ fontSize: 14, color: isDarkMode ? "#60a5fa" : "#2563eb", marginBottom: 2 }}>
                                          🧩 {c?.display_name || c?.name_localized || c?.localized_name || c?.name || `Collection ${c?.collection_id || c?.id}`}                                        </Text>
                                      </TouchableOpacity>
                                    ))}
                                  </View>
                                )}
                              </View>
                            );
                          })()}
                        </View>
                      </View>
                    ) : null}
                  </View>
                ))}
              </>
            )}
          </ScrollView>
        )}
      </View>

      {/* ====== CHAT INPUT FOOTER ====== */}
      {onInputSubmit && (
        <View
          style={{
            padding: isCompact ? 10 : 14,
            background: isDarkMode
              ? "linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(37,99,235,0.08) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(37,99,235,0.04) 100%)",
          }}
        >
          {/* "Ask me anything" label */}
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginBottom: isCompact ? 6 : 8 }}>
            <View
              style={{
                width: 6, height: 6, borderRadius: 3, backgroundColor: "#22c55e",
                ...(Platform.OS === "web" ? {
                  boxShadow: "0 0 6px rgba(34,197,94,0.6)",
                  animation: "pulse-dot 2s ease-in-out infinite",
                } : {}),
              }}
            />
            <Text style={{
              fontSize: isCompact ? 12 : 13, fontWeight: "700", letterSpacing: 0.5,
              textTransform: "uppercase", color: isDarkMode ? "#94a3b8" : "#64748b",
            }}>
              {t("Ask Saras AI anything")}
            </Text>
          </View>

          {/* Input bar */}
          <View style={{ flexDirection: "row", gap: 8, alignItems: "stretch" }}>
            <View
              style={{
                flex: 1, flexDirection: "row", alignItems: "center", gap: 8,
                backgroundColor: isDarkMode ? "#0f172a" : "#fff",
                borderWidth: 2,
                borderColor: isDarkMode ? "rgba(96,165,250,0.3)" : "rgba(37,99,235,0.2)",
                borderRadius: 14,
                paddingHorizontal: 14,
                ...(Platform.OS === "web" ? {
                  boxShadow: isDarkMode
                    ? "0 0 16px rgba(96,165,250,0.12), 0 2px 8px rgba(0,0,0,0.3)"
                    : "0 0 16px rgba(37,99,235,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                } : {}),
              }}
            >
              <Image
                source={require("../../assets/Saras-3D_logo_bird.png")}
                style={{ width: 22, height: 22 }}
                resizeMode="contain"
              />
              <TextInput
                value={inputValue || ""}
                onChangeText={(text) => setInputValue && setInputValue(text)}
                onSubmitEditing={() => {
                  if (inputValue?.trim()) {
                    setPanelMode("active");
                    onInputSubmit(inputValue.trim());
                    setInputValue && setInputValue("");
                  }
                }}
                placeholder={placeholder || t("Ask Saras AI a question...")}
                placeholderTextColor={isDarkMode ? "#475569" : "#94a3b8"}
                returnKeyType="send"
                blurOnSubmit={false}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: isCompact ? 14 : 16,
                  color: isDarkMode ? "#e2e8f0" : "#0f172a",
                  paddingVertical: isCompact ? 12 : 14,
                  paddingRight: 4,
                  fontWeight: "500",
                  ...(Platform.OS === "web" ? { outlineStyle: "none", textOverflow: "ellipsis", overflow: "hidden" } : {}),
                }}
              />
            </View>
            {/* Mic button */}
            {onVoiceInput && (
              <TouchableOpacity
                onPress={onVoiceInput}
                style={{
                  width: isCompact ? 44 : 50, borderRadius: 14,
                  backgroundColor: isListening
                    ? (isDarkMode ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.1)")
                    : (isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.08)"),
                  borderWidth: 1.5,
                  borderColor: isListening
                    ? "#ef4444"
                    : (isDarkMode ? "rgba(148,163,184,0.2)" : "rgba(148,163,184,0.2)"),
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                  ...(Platform.OS === "web" ? {
                    transition: "background-color 0.2s, border-color 0.2s",
                    cursor: "pointer",
                    ...(isListening ? { animation: "mic-pulse 1.5s ease-in-out infinite" } : {}),
                  } : {}),
                }}
              >
                <Text style={{
                  fontSize: isCompact ? 20 : 22,
                  color: isListening ? "#ef4444" : (isDarkMode ? "#94a3b8" : "#64748b"),
                }}>
                  {isListening ? "\uD83D\uDD34" : "\uD83C\uDF99\uFE0F"}
                </Text>
              </TouchableOpacity>
            )}
            {/* Mute/Unmute toggle button */}
            {onToggleMute && (
              <TouchableOpacity
                onPress={onToggleMute}
                accessibilityLabel={isMuted ? "Unmute audio" : "Mute audio"}
                accessibilityRole="button"
                style={{
                  width: isCompact ? 44 : 50, borderRadius: 14,
                  backgroundColor: isMuted
                    ? (isDarkMode ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)")
                    : (isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.08)"),
                  borderWidth: 1.5,
                  borderColor: isMuted
                    ? (isDarkMode ? "rgba(239,68,68,0.35)" : "rgba(239,68,68,0.3)")
                    : (isDarkMode ? "rgba(148,163,184,0.2)" : "rgba(148,163,184,0.2)"),
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                  ...(Platform.OS === "web" ? {
                    transition: "background-color 0.2s, border-color 0.2s",
                    cursor: "pointer",
                  } : {}),
                }}
              >
                <MaterialCommunityIcons
                  name={isMuted ? "volume-off" : "volume-high"}
                  size={isCompact ? 20 : 22}
                  color={isMuted ? "#ef4444" : (isDarkMode ? "#94a3b8" : "#64748b")}
                />
              </TouchableOpacity>
            )}
            {/* Send button */}
            <TouchableOpacity
              onPress={() => {
                if (inputValue?.trim()) {
                  setPanelMode("active");
                  onInputSubmit(inputValue.trim());
                  setInputValue && setInputValue("");
                }
              }}
              style={{
                width: isCompact ? 44 : 50, borderRadius: 14,
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                alignItems: "center", justifyContent: "center", flexShrink: 0,
                ...(Platform.OS === "web" ? {
                  boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  cursor: "pointer",
                } : {}),
              }}
            >
              <Text style={{ color: "#fff", fontSize: isCompact ? 20 : 22, fontWeight: "700" }}>➤</Text>
            </TouchableOpacity>
          </View>

          {/* Keyboard hint row */}
          <View style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            marginTop: 8,
          }}>
            {/* Keyboard hint */}
            <Text style={{
              fontSize: 12, color: isDarkMode ? "#475569" : "#94a3b8",
            }}>
              ⏎ {t("Enter to send")}
            </Text>
          </View>
        </View>
      )}

      {/* CSS animations — typing-dot keyframes live in ChatPanel.js (injected once via document.head) */}
      {Platform.OS === "web" && (
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes pulse-dot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.4); }
          }
          @keyframes mic-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
            50% { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
          }
        `}} />
      )}

    </View>
  );
}

export default SuggestionRail;
